import React, { useState } from 'react';
import { Clock, Activity as ActivityIcon, Moon, Heart, Brain, Zap, Flame, Timer, ArrowRight } from 'lucide-react';
import { ActivityDetails } from './ActivityDetails';

interface LoggedActivity {
  id: number;
  type: 'workout' | 'sleep' | 'recovery';
  name: string;
  startTime: string;
  endTime: string;
  duration: number;
  metrics?: {
    calories?: number;
    avgHeartRate?: number;
    strain?: number;
    stressLevel?: {
      before: number;
      after: number;
    };
    sleepStages?: {
      rem: number;
      deep: number;
      light: number;
      awake: number;
    };
  };
}

const activities: LoggedActivity[] = [
  {
    id: 1,
    type: 'sleep',
    name: 'Night Sleep',
    startTime: '23:00',
    endTime: '06:30',
    duration: 450,
    metrics: {
      avgHeartRate: 62,
      sleepStages: {
        rem: 108,
        deep: 98,
        light: 224,
        awake: 20
      }
    }
  },
  {
    id: 2,
    type: 'workout',
    name: 'Morning Run',
    startTime: '06:45',
    endTime: '07:30',
    duration: 45,
    metrics: {
      calories: 420,
      avgHeartRate: 145,
      strain: 12.4
    }
  },
  {
    id: 3,
    type: 'recovery',
    name: 'Meditation',
    startTime: '07:45',
    endTime: '08:00',
    duration: 15,
    metrics: {
      avgHeartRate: 68,
      stressLevel: {
        before: 72,
        after: 45
      }
    }
  },
  {
    id: 4,
    type: 'workout',
    name: 'Weight Training',
    startTime: '18:00',
    endTime: '19:00',
    duration: 60,
    metrics: {
      calories: 280,
      avgHeartRate: 128,
      strain: 8.6
    }
  }
].sort((a, b) => {
  // Keep sleep at the top
  if (a.type === 'sleep') return -1;
  if (b.type === 'sleep') return 1;
  
  // Convert time strings to comparable values
  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
});

export function MyActivities() {
  const [selectedActivity, setSelectedActivity] = useState<LoggedActivity | null>(null);

  const getActivityIcon = (type: LoggedActivity['type']) => {
    switch (type) {
      case 'workout':
        return <ActivityIcon className="w-4 h-4 text-primary" />;
      case 'sleep':
        return <Moon className="w-4 h-4 text-purple-500" />;
      case 'recovery':
        return <Heart className="w-4 h-4 text-success" />;
    }
  };

  const getMetricDisplay = (activity: LoggedActivity) => {
    const metrics = [];

    if (activity.metrics?.calories) {
      metrics.push(
        <div key="calories" className="flex items-center gap-0.5 bg-orange-500/10 px-1.5 py-0.5 rounded-full text-[10px]">
          <Flame className="w-2.5 h-2.5 text-orange-500" />
          <span className="text-orange-500">{activity.metrics.calories}</span>
        </div>
      );
    }

    if (activity.metrics?.strain) {
      metrics.push(
        <div key="strain" className="flex items-center gap-0.5 bg-primary/10 px-1.5 py-0.5 rounded-full text-[10px]">
          <Zap className="w-2.5 h-2.5 text-primary" />
          <span className="text-primary">{activity.metrics.strain.toFixed(1)}</span>
        </div>
      );
    }

    if (activity.metrics?.stressLevel) {
      const reduction = activity.metrics.stressLevel.before - activity.metrics.stressLevel.after;
      metrics.push(
        <div key="stress" className="flex items-center gap-0.5 bg-success/10 px-1.5 py-0.5 rounded-full text-[10px]">
          <Brain className="w-2.5 h-2.5 text-success" />
          <span className="text-success">-{reduction}%</span>
        </div>
      );
    }

    if (activity.metrics?.sleepStages) {
      const deepSleepHours = (activity.metrics.sleepStages.deep / 60).toFixed(1);
      metrics.push(
        <div key="sleep" className="flex items-center gap-0.5 bg-purple-500/10 px-1.5 py-0.5 rounded-full text-[10px]">
          <Moon className="w-2.5 h-2.5 text-purple-500" />
          <span className="text-purple-500">{deepSleepHours}h</span>
        </div>
      );
    }

    return metrics;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium">Today's Activities</h3>
        </div>
        <span className="text-xs text-muted-foreground">{activities.length} activities</span>
      </div>

      <div className="space-y-1.5">
        {activities.map((activity) => (
          <button
            key={activity.id}
            onClick={() => setSelectedActivity(activity)}
            className="w-full bg-muted/30 p-2.5 rounded-lg text-left hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  {getActivityIcon(activity.type)}
                  <div className="absolute inset-0 bg-current opacity-20 blur-lg rounded-full" />
                </div>
                <div>
                  <div className="text-sm font-medium">{activity.name}</div>
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Timer className="w-3 h-3" />
                    <span>{activity.startTime}</span>
                    <ArrowRight className="w-2.5 h-2.5" />
                    <span>{activity.endTime}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {getMetricDisplay(activity)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selectedActivity && (
        <ActivityDetails
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
        />
      )}
    </div>
  );
}